import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';
import {
  showCustomNotification,
  showNotification,
  showError,
} from '@vk-hr-tek/core/notifications';

import {
  CompanyEventsStat,
  CompanyGroupUsers,
  GetCompanyParams,
  GetCompanyResponse,
  Group,
  SettingsService,
} from '@app/gen/settings';
import { Entity } from '@app/types';
import {
  AccountGroupsWithCount,
  AccountService,
  EditableAccount,
  GetAccountCompaniesResponse,
  GetAccountGroupsCountersParams,
  GetAccountsResponse,
  UpdateAccountSettingsParams,
  UpdateAccountSettingsRequest,
} from '@app/gen/account';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';

import {
  accountsAdapter,
  SettingsState,
  SettingsWithRootState,
} from '../../settings.state';
import { AddRoleAccountDto } from '../../../dto';

const MASCULINE_SETTINGS_TYPE = [
  'search_users_enabled',
  'business_trips_enabled',
];

export const getAccounts = createAsyncThunk<
  GetAccountsResponse['accounts'],
  void,
  ThunkExtra<SettingsWithRootState>
>('settings/getAccounts', async (_, { rejectWithValue, extra: { inject } }) => {
  try {
    const accoutService = inject(AccountService);

    const result = await accoutService.getAccounts();

    return result.accounts;
  } catch (err) {
    return rejectWithValue(classToPlain(err) as AppError);
  }
});

export const getAccountCompanies = createAsyncThunk<
  GetAccountCompaniesResponse['companies'],
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountCompanies',
  async (accountId, { rejectWithValue, extra: { inject } }) => {
    try {
      const accoutService = inject(AccountService);

      const result = await accoutService.getAccountCompanies({
        params: { account_id: accountId },
      });

      return result.companies;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateAccountRoleModelEnabled = createAsyncThunk<
  void,
  UpdateAccountSettingsParams & UpdateAccountSettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateAccountRoleModelEnabled',
  async (
    { account_id, role_model_enabled },
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const accountService = inject(AccountService);

      const account = getState().settings.accountsWithSettings.accounts.find(
        ({ account_item }) => account_item.id === account_id,
      );

      await accountService.updateAccountSettings({
        params: { account_id },
        body: {
          role_model_enabled,
          ...(!role_model_enabled &&
            account?.search_users_enabled && { search_users_enabled: false }),
        },
      });

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: `Использование ролевой модели ${
              role_model_enabled ? 'включено' : 'выключено'
            }`,
            subtitle: 'Аккаунт',
            message: account?.account_item.name ?? '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось ${
              role_model_enabled ? 'включить' : 'выключить'
            } использование ролевой модели`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAccountSettings = createAsyncThunk<
  EditableAccount[],
  void,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountSettings',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const accountService = inject(AccountService);

      const result = await accountService.getAccountsFindByGroupAdmin();

      return result.accounts;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAccountInfo = createAsyncThunk<
  void,
  string | undefined,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountInfo',
  async (accountId, { rejectWithValue, dispatch }) => {
    try {
      const accounts = await awaitDispatch(dispatch(getAccounts()));

      if (accountId || accounts[0]) {
        await Promise.all([
          awaitDispatch(
            dispatch(getAccountCompanies(accountId ?? accounts[0].id)),
          ),
          awaitDispatch(dispatch(getAccountSettings())),
        ]);
      }
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateAccountSetting = createAsyncThunk<
  undefined,
  {
    accountId: string;
    settings?: UpdateAccountSettingsRequest;
    notificationInfo: {
      accountName?: string;
      settingName: string;
      isEnabled: boolean;
      settingType?: string;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateAccountSettings',
  async (
    { accountId, settings, notificationInfo },
    { rejectWithValue, extra: { inject }, dispatch },
  ) => {
    try {
      const accountService = inject(AccountService);

      await accountService.updateAccountSettings({
        params: { account_id: accountId },
        body: settings,
      });

      const isMasculine =
        notificationInfo.settingType &&
        Number(MASCULINE_SETTINGS_TYPE.includes(notificationInfo.settingType));

      const enableText = `${notificationInfo.isEnabled ? 'включен' : 'выключен'}${isMasculine ? '' : 'o'}`;

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: `${notificationInfo.settingName} ${enableText}`,
            subtitle: 'Аккаунт',
            message: notificationInfo.accountName || '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось ${notificationInfo.isEnabled ? 'включить' : 'выключить'} ${notificationInfo.settingName.toLowerCase()}`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompany = createAsyncThunk<
  GetCompanyResponse,
  GetCompanyParams,
  ThunkExtra
>(
  'settings/getCompany',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      const result = await service.getCompany({
        params: { company_id: dto.company_id },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAccountGroups = createAsyncThunk<
  Group[],
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountGroups',
  async (accountId, { rejectWithValue, extra: { inject } }) => {
    try {
      const accountService = inject(AccountService);

      const result = await accountService.getAccountGroups({
        params: { account_id: accountId },
      });

      return result.groups;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAccountGroupsWithCount = createAsyncThunk<
  AccountGroupsWithCount,
  GetAccountGroupsCountersParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountGroupsWithCount',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      return await inject(AccountService).getAccountGroupsCounters({ params });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addAccountGroup = createAsyncThunk<
  void,
  ThunkValuesAndActions<AddRoleAccountDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addAccountGroup',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const accountService = inject(AccountService);

      await accountService.createAccountGroup({
        params: { account_id: values.accountId },
        body: {
          name: values.roleName,
          parent_id: values.parentRoleId,
        },
      });

      dispatch(showNotification(`Роль добавлена \n ${values.roleName}`));

      actions.resolve(null);
    } catch (err) {
      dispatch(
        showError(
          'Не удалось добавить роль \n Попробуйте, пожалуйста, еще раз',
        ),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAccountReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAccounts.pending, (state) => {
    state.accounts.status = 'loading';
    state.accounts.error = null;
  });
  builder.addCase(getAccounts.fulfilled, (state, { payload }) => {
    accountsAdapter.setAll(state.accounts, payload);
    state.accounts.status = 'complete';
    state.accounts.error = null;
  });
  builder.addCase(getAccounts.rejected, (state, { payload, error }) => {
    accountsAdapter.setAll(state.accounts, []);
    state.accounts.status = 'failed';
    state.accounts.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getAccountSettings.pending, (state) => {
    state.accountsWithSettings.status = 'loading';
    state.accountsWithSettings.error = null;
  });
  builder.addCase(getAccountSettings.fulfilled, (state, { payload }) => {
    state.accountsWithSettings.accounts = payload;
    state.accountsWithSettings.status = 'complete';
    state.accountsWithSettings.error = null;
  });
  builder.addCase(getAccountSettings.rejected, (state, { payload, error }) => {
    state.accountsWithSettings.status = 'failed';
    state.accountsWithSettings.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getAccountCompanies.pending, (state) => {
    state.accountSettings.accountCompanies = {
      data: [],
      status: 'loading',
      error: null,
    };
  });
  builder.addCase(getAccountCompanies.fulfilled, (state, { payload }) => {
    state.accountSettings.accountCompanies = {
      data: payload,
      status: 'complete',
      error: null,
    };
  });
  builder.addCase(getAccountCompanies.rejected, (state, { payload, error }) => {
    state.accountSettings.accountCompanies = {
      data: [],
      status: 'failed',
      error:
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError),
    };
  });

  builder.addCase(getAccountInfo.pending, (state) => {
    state.accountSettings.status = 'loading';
    state.accountSettings.error = null;
  });
  builder.addCase(getAccountInfo.fulfilled, (state) => {
    state.accountSettings.status = 'complete';
    state.accountSettings.error = null;
  });
  builder.addCase(getAccountInfo.rejected, (state, { payload, error }) => {
    state.accountSettings.status = 'failed';
    state.accountSettings.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(updateAccountRoleModelEnabled.pending, (state, { meta }) => {
    accountsAdapter.updateOne(state.accounts, {
      id: meta.arg.account_id,
      changes: { role_model_enabled: meta.arg.role_model_enabled },
    });
  });
  builder.addCase(updateAccountRoleModelEnabled.rejected, (state, { meta }) => {
    accountsAdapter.updateOne(state.accounts, {
      id: meta.arg.account_id,
      changes: { role_model_enabled: !meta.arg.role_model_enabled },
    });
  });
  builder.addCase(
    updateAccountRoleModelEnabled.fulfilled,
    (state, { meta }) => {
      state.accountsWithSettings.accounts =
        state.accountsWithSettings.accounts.map((props) => {
          if (props.account_item.id === meta.arg.account_id) {
            return {
              ...props,
              ...(!meta.arg.role_model_enabled && {
                search_users_enabled: false,
              }),
            };
          }

          return props;
        });
    },
  );

  builder.addCase(updateAccountSetting.fulfilled, (state, { meta }) => {
    state.accountsWithSettings.accounts =
      state.accountsWithSettings.accounts.map((props) => {
        if (props.account_item.id === meta.arg.accountId) {
          return {
            ...props,
            ...meta.arg.settings,
          };
        }

        return props;
      });
  });

  builder.addCase(getAccountGroups.pending, (state) => {
    state.accountGroups.status = 'loading';
    state.accountGroups.error = null;
  });
  builder.addCase(getAccountGroups.fulfilled, (state, { payload }) => {
    state.accountGroups.status = 'complete';
    state.accountGroups.groups = payload;
    state.accountGroups.error = null;
  });
  builder.addCase(getAccountGroups.rejected, (state, { payload, error }) => {
    state.accountGroups.status = 'failed';
    state.accountGroups.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getAccountGroupsWithCount.pending, (state) => {
    state.accountGroupsWithCount.status = 'loading';
    state.accountGroupsWithCount.error = null;
  });
  builder.addCase(getAccountGroupsWithCount.fulfilled, (state, { payload }) => {
    state.accountGroupsWithCount.status = 'complete';
    state.accountGroupsWithCount.groups = payload.groups;
    state.accountGroupsWithCount.error = null;
  });
  builder.addCase(
    getAccountGroupsWithCount.rejected,
    (state, { payload, error }) => {
      state.accountGroupsWithCount.status = 'failed';
      state.accountGroupsWithCount.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(getCompany.pending, (state) => {
    state.accountCompany.status = 'loading';
  });

  builder.addCase(getCompany.fulfilled, (state, action) => {
    state.accountCompany.status = 'complete';
    state.accountCompany.data = action.payload;
    state.accountCompany.isSingleCompany =
      !!action.payload.account_with_one_company;
  });

  builder.addCase(getCompany.rejected, (state) => {
    state.accountCompany.status = 'failed';
  });
};
